// The frame and its render passes (the runtime design §5).
//
// WebGL2 has one implicit frame: a draw goes wherever the framebuffer
// binding points. WebGPU has neither. Every draw belongs to a RENDER PASS,
// a pass belongs to a command encoder, and an encoder reaches the device
// only at `submit`. So the loop's `beginFrame` opens an encoder and the
// canvas pass, and `endFrame` closes them and submits.
//
// Two things ride on the open pass and nothing else knows them:
//
//  1. **The target key.** A render pipeline is built for the attachment
//     formats of the pass it draws into, so the pipeline cache is keyed by
//     the key this file states.
//  2. **The blend flag.** The engine has ONE blend state, and `#blend=0`
//     turns it off. WebGPU carries blend on the PIPELINE and not as state,
//     so the flag is the second half of the cache key.

import type { GpuTimestampRing } from '../../perf/gputimer-gpu';
import type { UniformArena } from './arena';

/** What the frame is told about the surface it draws on. */
export interface FrameDeps {
  readonly canvasContext: GPUCanvasContext;
  /** The canvas format, which is half of the canvas pass's key. */
  readonly canvasFormat: GPUTextureFormat;
  readonly arena: UniformArena;
  /** The live timestamp ring, or null where the device has no feature. */
  timer(): GpuTimestampRing | null;
  /** The drawing surface in device px. */
  pixelSize(): readonly [number, number];
  /** The size of the canvas texture the last frame drew into (R1498). */
  readonly drawn: DrawnProbe;
}

/**
 * The canvas texture of the last frame, recorded for the touch log alone
 * (R1498). `on` is null while nobody watches, so an unwatched frame reads
 * no size; a watched frame records the size and calls `on` when it changed.
 */
export interface DrawnProbe {
  w: number;
  h: number;
  on: (() => void) | null;
}

/** What one open pass writes into. The pipeline cache reads both. */
export interface PassTarget {
  /** The key half of the pipeline cache: the attachments, as a string. */
  readonly key: string;
  readonly formats: readonly GPUTextureFormat[];
}

export class GpuFrame {
  /** Is the one blend state on? `#blend=0` turns it off. */
  blend = true;

  private encoder: GPUCommandEncoder | null = null;
  private current: GPURenderPassEncoder | null = null;
  private target: PassTarget;
  private canvasView: GPUTextureView | null = null;
  private readonly canvasTarget: PassTarget;

  constructor(private readonly device: GPUDevice, private readonly deps: FrameDeps) {
    this.canvasTarget = {
      key: `canvas:${deps.canvasFormat}`,
      formats: [deps.canvasFormat],
    };
    this.target = this.canvasTarget;
  }

  /** The open pass, or null between frames. Every draw goes through it. */
  get pass(): GPURenderPassEncoder | null { return this.current; }

  /** What the open pass draws into. The pipeline cache is keyed by it. */
  get passTarget(): PassTarget { return this.target; }

  /** Is a frame open? A draw outside one is dropped. */
  get open(): boolean { return this.encoder !== null; }

  /**
   * Opens the frame: the arena, the encoder and the canvas pass.
   *
   * The canvas view is taken ONCE. `getCurrentTexture` gives the same texture
   * for the whole frame.
   */
  begin(clear: boolean, clearRgb: readonly [number, number, number]): void {
    this.deps.arena.reset();
    this.blend = true;
    this.encoder = this.device.createCommandEncoder({ label: 'fast: frame' });
    const tex = this.deps.canvasContext.getCurrentTexture();
    const probe = this.deps.drawn;
    if (probe.on !== null && (tex.width !== probe.w || tex.height !== probe.h)) {
      probe.w = tex.width;
      probe.h = tex.height;
      probe.on();
    }
    this.canvasView = tex.createView();
    this.openCanvasPass(clear ? clearRgb : null);
  }

  /**
   * Closes the frame: the pass, the timer, the arena and the submit.
   *
   * The arena is written BEFORE the submit, because a queue write made
   * before a submit is ordered in front of everything the submit carries.
   */
  end(): void {
    const encoder = this.encoder;
    if (!encoder) return;
    this.closePass();
    const timer = this.deps.timer();
    timer?.resolve(encoder);
    this.deps.arena.flush(this.device.queue);
    this.device.queue.submit([encoder.finish()]);
    this.encoder = null;
    this.canvasView = null;
    timer?.poll();
  }

  /**
   * Opens the canvas pass. `clearRgb` fills it; null keeps what is there.
   *
   * The alpha of the clear is 1, because the canvas is configured opaque and
   * the background pass paints the same pixels a moment later.
   */
  openCanvasPass(clearRgb: readonly [number, number, number] | null): void {
    const encoder = this.encoder;
    const view = this.canvasView;
    if (!encoder || !view) return;
    const attachment: GPURenderPassColorAttachment = clearRgb
      ? {
        view,
        loadOp: 'clear',
        storeOp: 'store',
        clearValue: { r: clearRgb[0], g: clearRgb[1], b: clearRgb[2], a: 1 },
      }
      : { view, loadOp: 'load', storeOp: 'store' };
    this.openPass({ label: 'fast: canvas', colorAttachments: [attachment] }, this.canvasTarget);
    const [w, h] = this.deps.pixelSize();
    this.current?.setViewport(0, 0, w, h, 0, 1);
  }

  /**
   * Opens one pass and attaches the timestamps of the frame to it.
   *
   * Every pass of a frame is measured, so `gpuMs` holds the clear and not
   * the draw pass alone (R431).
   */
  openPass(desc: GPURenderPassDescriptor, target: PassTarget): void {
    const encoder = this.encoder;
    if (!encoder) return;
    this.closePass();
    const writes = this.deps.timer()?.passTimestamps();
    this.current = encoder.beginRenderPass(
      writes ? { ...desc, timestampWrites: writes } : desc);
    this.target = target;
  }

  /** Closes the open pass. A second call does nothing. */
  closePass(): void {
    if (!this.current) return;
    this.current.end();
    this.current = null;
  }

  /** Drops the frame without submitting it. The device loss path calls it. */
  abandon(): void {
    this.current = null;
    this.encoder = null;
    this.canvasView = null;
    this.target = this.canvasTarget;
    this.blend = true;
  }
}
